import { useEffect, useRef, useState } from 'react';
import { customerDisplayName } from '../../utils/customerView';

const INACTIVE_STATUSES = ['inactive', 'suspended', 'cancelled', 'expired', 'disabled'];
export const isActiveStatus = (status) => !INACTIVE_STATUSES.includes((status || 'active').toLowerCase());

export const initialsOf = (name) => String(name || '')
  .split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

export const formatDate = (date) => (date
  ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '-');

// "Today", "Yesterday", "3 days ago", then the date.
export const relativeDate = (value, now = new Date()) => {
  if (!value) return '';
  const date = new Date(value);
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(date.getFullYear(), date.getMonth(), date.getDate())) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return `${days} days ago`;
  return formatDate(date);
};

export function StatusChip({ status }) {
  const label = status || 'Active';
  const tone = isActiveStatus(label) ? 'ok' : (['suspended', 'cancelled'].includes(label.toLowerCase()) ? 'bad' : 'muted');
  return <span className={`cx-chip cx-chip-${tone}`}>{label}</span>;
}

// Copies text and says so for a moment. Falls back quietly where the
// clipboard API is unavailable (older browsers, insecure origins).
export function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      const area = document.createElement('textarea');
      area.value = value;
      document.body.appendChild(area);
      area.select();
      try { document.execCommand('copy'); } catch (e) { /* nothing more to try */ }
      area.remove();
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button type="button" className={`cx-copy ${copied ? 'is-copied' : ''}`} onClick={copy} aria-label={`Copy ${label}`}>
      <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true"></i>
      <span className="cx-copy-text" aria-live="polite">{copied ? 'Copied' : ''}</span>
    </button>
  );
}

// The avatar + name + status block at the top of the page.
export function CustomerHeading({ customer }) {
  const name = customerDisplayName(customer);
  const person = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
  return (
    <div className="cx-heading">
      <div className="cx-avatar" aria-hidden="true">{initialsOf(person || name)}</div>
      <div className="cx-heading-text">
        <div className="cx-title-row">
          <h1 className="cx-title">{name}</h1>
          <StatusChip status={customer.status} />
        </div>
        <p className="cx-sub">
          {customer.companyName && person ? `${person} · ` : ''}Customer since {formatDate(customer.createdAt)}
        </p>
      </div>
    </div>
  );
}

// Headline numbers; each tile jumps to where the detail is.
export function KpiStrip({ items }) {
  return (
    <div className="cx-kpis" role="list">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="listitem"
          className={`cx-kpi ${item.tone ? `is-${item.tone}` : ''}`}
          onClick={item.onClick}
        >
          <span className="cx-kpi-label">{item.label}</span>
          <span className="cx-kpi-value">{item.value}</span>
          {item.sub && <span className="cx-kpi-sub">{item.sub}</span>}
        </button>
      ))}
    </div>
  );
}

// Tabs with proper keyboard support: arrows, Home and End move between them.
export function Tabs({ tabs, active, onChange, idPrefix = 'cx' }) {
  const onKeyDown = (e, index) => {
    const last = tabs.length - 1;
    let next = null;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    onChange(tabs[next].key);
    document.getElementById(`${idPrefix}-tab-${tabs[next].key}`)?.focus();
  };

  return (
    <div className="cx-tabs" role="tablist" aria-label="Customer sections">
      {tabs.map((tab, index) => (
        <button
          key={tab.key}
          id={`${idPrefix}-tab-${tab.key}`}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          aria-controls={`${idPrefix}-panel-${tab.key}`}
          tabIndex={active === tab.key ? 0 : -1}
          className={`cx-tab ${active === tab.key ? 'is-active' : ''}`}
          onClick={() => onChange(tab.key)}
          onKeyDown={(e) => onKeyDown(e, index)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="cx-tab-count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ icon, title, children }) {
  return (
    <div className="cx-empty">
      <i className={`fas ${icon}`} aria-hidden="true"></i>
      <p className="cx-empty-title">{title}</p>
      {children && <p className="cx-empty-text">{children}</p>}
    </div>
  );
}
